import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { MainPageBase } from "../page-bases/MainPageBase";
import { Box, Divider, Grid, Stack } from "@mui/material";
import dayjs from "dayjs";

import { AssetTag, CreateAsset } from "../../config/types";
import { ExpandableContainer } from "../../components/Collapsible/ExpandableContainer";
import ItemInput from "../../components/Input/ItemInput";
import { useDmsForm } from "../../hooks/useDmsFrom";
import { BatchSelectInput } from "../../common-ui/select-input-models/BatchSelectInput";
import { Field } from "../../utils/formSchema";
import { AssetCategorySelectInput } from "../../common-ui/select-input-models/AssetCategorySelectInput";
import { assetApi } from "../../services/assets/asset-crud";
import { AssetCategoryApi } from "../../services/assets/assetCategory";
import { AssetTagApi } from "../../services/assets/assetTag";
import ItemSelectInput from "../../components/Input/ItemSelectInput";
import { MultiSelectInput } from "../../components/Input/MultiSelectInput";
import { DatePicker } from "../../components/calender";
import VerticalGap from "../../components/Gap/VerticalGap";
import { actionDialogStore, alertDialogStore } from "../../store/dialogs";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  ASSET_OWNERSHIP_LIST,
  ASSET_STATUS_LIST,
  ASSET_BRAND_NAME,
} from "../../config/constants/common";
import { updateAssetDetail } from "../../store/reducers/assets/assetDetail";
import useAppNavigation from "../../hooks/useAppNavigation";
import appRoutePath from "../../config/path";

const defaultValue = <T,>(value: T) => ({ isValid: true, value });

function UpdateAssetPage() {
  const assetDetailState = useAppSelector((data) => data.assetDetail);

  const {
    form,
    updateForm,
    updateInputField,
    updateNestedArrayWithInputField,
    fieldHasError,
    setSubmitAttempt,
    onFormSubmit,
  } = useDmsForm<CreateAsset>({
    serial_number: "",
    ownership: "",
    embossment_number: "",
    model: "",
    brand: "",
    description: "",
    warranty_in_months: 0,
    asset_status: "",
    batch_id: "",
    asset_category_id: "",
    manufactured_date: "",
    date_received: "",
    meta: [],
  });

  const [assetTagList, setTagList] = useState<AssetTag[]>([]);
  const [isTypePhysical, setCategoryType] = useState<boolean | undefined>(
    undefined,
  );
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useAppNavigation();

  const target = useParams<{ id: string }>();
  const dispatch = useAppDispatch();

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        setIsLoading(true);
        const res = await assetApi.getAsset(target?.id ?? "");
        if (res.data) {
          dispatch(updateAssetDetail(res.data));

          updateForm({
            serial_number: defaultValue(res.data.serial_number ?? ""),
            ownership: defaultValue(res.data.ownership ?? ""),
            embossment_number: defaultValue(res.data.embossment_number ?? ""),
            model: defaultValue(res.data.model ?? ""),
            brand: defaultValue(res.data.brand ?? ""),
            description: defaultValue(res.data.description ?? ""),
            warranty_in_months: defaultValue(res.data.warranty_in_months ?? 0),
            asset_status: defaultValue(res.data.asset_status ?? ""),
            batch_id: defaultValue(res.data.batch_id ?? ""),
            asset_category_id: defaultValue(res.data.asset_category_id ?? ""),
            manufactured_date: defaultValue(res.data.manufactured_date ?? ""),
            date_received: defaultValue(res.data.date_received ?? ""),
            meta: defaultValue(res.data.asset_meta ?? []),
            submitAttempt: false,
          });
        }
      } catch (error) {
        alertDialogStore.open({
          title: "Failed to Load Asset Details",
          subtitle: "Unable to fetch asset details. Please try again.",
          isSuccess: false,
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (target?.id) {
      fetchDetail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.id]);

  useEffect(() => {
    if (form.asset_category_id.value === "") {
      return;
    }
    const fetchMeta = async () => {
      const res = await AssetCategoryApi.getAssetCategoryTags(
        form.asset_category_id.value,
      );
      if (res.data) {
        let tags = res.data;

        const enrichedTags = await Promise.all(
          tags.map(async (tag): Promise<AssetTag> => {
            try {
              const allTagsRes = await AssetTagApi.getAssetTags();
              if (allTagsRes.data && Array.isArray(allTagsRes.data)) {
                const matchedTag = allTagsRes.data.find(
                  (t) => t.name === tag.name,
                );
                if (matchedTag) {
                  return {
                    ...tag,
                    asset_tag_field_type: matchedTag.asset_tag_field_type as
                      | "text-input"
                      | "number-input"
                      | "single-select"
                      | "multi-select",
                    asset_tag_options: matchedTag.asset_tag_options,
                  };
                }
              }
            } catch (error) {}
            return tag as AssetTag;
          }),
        );

        setTagList(enrichedTags);
        updateForm((p) => ({
          ...p,
          meta: {
            isValid: true,
            errorMessage: "",
            value: enrichedTags.map((tag) => {
              return {
                asset_category_tag_id: tag.asset_category_tag_id,
                meta_value:
                  assetDetailState.asset_meta?.find(
                    (item) =>
                      item.asset_category_tag_id === tag.asset_category_tag_id,
                  )?.meta_value ?? "",
              };
            }),
          },
        }));
      }
    };
    fetchMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.asset_category_id.value]);

  function cancelAddItem() {
    alertDialogStore.open({
      title: "Unsaved Changes",
      subtitle: `Do you want to discard changes?`,
      onRightBtnPress: () =>
        navigate({ path: appRoutePath.ASSET_REGISTRY_HISTORY_PAGE }),
      isSuccess: false,
    });
  }

  async function toggleUpdateBtn() {
    const excludeField: (keyof CreateAsset)[] = isTypePhysical
      ? [
          "meta",
          "description",
          "warranty_in_months",
          "embossment_number",
          "manufactured_date",
        ]
      : [
          "meta",
          "description",
          "embossment_number",
          "manufactured_date",
          "warranty_in_months",
        ];
    setSubmitAttempt(true);
    const { data, isValid } = onFormSubmit({ excludeFields: excludeField });

    if (isValid) {
      const updateRequestFtn = async () => {
        const response = await assetApi.updateAsset(target.id ?? "", data);
        alertDialogStore.open({
          title: "Update Asset",
          subtitle: response.message,
          rightBtnTxt: `${response.success ? "See All Asset" : "Try again"}`,
          onRightBtnPress: async () => {
            if (response.success) {
              navigate({ path: appRoutePath.ASSET_REGISTRY_HISTORY_PAGE });
            }
          },
          isSuccess: response.success,
        });
      };

      actionDialogStore.open({
        title: "Update Asset",
        subtitle: 'Click "Confirm" to update asset',
        onRightBtnPress: updateRequestFtn,
      });
    }
  }

  function handleBatchSelect(item: Field) {
    updateForm((p) => ({ ...p, batch_id: item }));
  }

  function handleAssetCatSelect(item: Field, isPhysical?: boolean) {
    updateForm((p) => ({ ...p, asset_category_id: item }));
    setCategoryType(isPhysical);
  }

  const handleManufacturedDatePicker = (date: string) => {
    updateForm((p) => ({
      ...p,
      manufactured_date: { value: date, isValid: true, errorMessage: "" },
    }));
  };

  const handleDateReceivedPicker = (date: string) => {
    updateForm((p) => ({
      ...p,
      date_received: { value: date, isValid: true, errorMessage: "" },
    }));
  };

  const renderDynamicAssetTagInput = (tag: AssetTag, index: number) => {
    const fieldType = tag.asset_tag_field_type;
    const metaValue = form.meta.value[index]?.meta_value ?? "";
    const handleMetaChange = updateNestedArrayWithInputField<
      CreateAsset,
      "meta"
    >("meta", index, "meta_value");

    const options = Array.isArray(tag.asset_tag_options)
      ? tag.asset_tag_options
      : tag.asset_tag_options
        ? [tag.asset_tag_options]
        : [];

    switch (fieldType) {
      case "text-input":
        return (
          <ItemInput
            key={`${tag.asset_category_tag_id}-${index}`}
            label={tag.name}
            placeholder={`Enter ${tag.name}`}
            value={metaValue}
            handleChange={handleMetaChange}
            size="full"
            required
          />
        );

      case "number-input":
        return (
          <ItemInput
            key={`${tag.asset_category_tag_id}-${index}`}
            label={tag.name}
            placeholder={`Enter ${tag.name}`}
            value={metaValue}
            handleChange={handleMetaChange}
            type="number"
            size="full"
            required
          />
        );

      case "single-select":
        const singleSelectOptions = options.map((option) => ({
          label: option,
          value: option,
        }));
        return (
          <ItemSelectInput
            key={`${tag.asset_category_tag_id}-${index}`}
            label={tag.name}
            placeholder={`Select ${tag.name}`}
            items={singleSelectOptions}
            item=""
            value={metaValue}
            handleChange={handleMetaChange}
            required
          />
        );

      case "multi-select":
        const multiSelectOptions = options.map((option) => ({
          label: option,
          value: option,
        }));
        const multiSelectValue = Array.isArray(metaValue)
          ? metaValue
          : metaValue
            ? metaValue.split(",").map((v) => v.trim())
            : [];
        return (
          <MultiSelectInput
            key={`${tag.asset_category_tag_id}-${index}`}
            label={tag.name}
            placeholder={`Select ${tag.name}`}
            items={multiSelectOptions}
            value={multiSelectValue}
            handleChange={(selectedValues) =>
              handleMetaChange({
                target: { value: selectedValues.join(",") },
              } as any)
            }
            required
          />
        );

      default:
        return (
          <ItemInput
            key={`${tag.asset_category_tag_id}-${index}`}
            label={tag.name}
            placeholder={`Enter ${tag.name} detail`}
            value={metaValue}
            handleChange={handleMetaChange}
            size="full"
            required
          />
        );
    }
  };

  if (isLoading) {
    return null;
  }

  return (
    <MainPageBase
      title={"Update Asset"}
      pageState={"add-item"}
      cancelBtnClick={cancelAddItem}
      addItemBtnOnClick={toggleUpdateBtn}
    >
      <Box height={`100%`} display={"flex"} justifyContent={"center"}>
        <Box width={"100%"} m={"0px 32px"}>
          <Box>
            <Stack direction={"row"} gap={4}>
              <ExpandableContainer
                initOpenState={true}
                label={"Asset Information"}
              >
                <Box columnGap={4} pb={3} pr={3}>
                  {/* First row of 3 fields */}
                  <Grid container spacing={3} pt={3}>
                    <Grid item xs={12} md={4}>
                      <ItemSelectInput
                        label="Asset Status"
                        placeholder="Select Asset Status"
                        items={ASSET_STATUS_LIST}
                        item=""
                        value={form.asset_status.value}
                        error={fieldHasError("asset_status")}
                        handleChange={updateInputField("asset_status")}
                        errorMessage={form.asset_status.errorMessage}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <ItemSelectInput
                        label="Ownership"
                        placeholder="Select Ownership"
                        items={ASSET_OWNERSHIP_LIST}
                        item=""
                        value={form.ownership.value}
                        error={fieldHasError("ownership")}
                        handleChange={updateInputField("ownership")}
                        errorMessage={form.ownership.errorMessage}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <AssetCategorySelectInput
                        placeholder={"Select Asset Category"}
                        label={"Asset Category"}
                        handleChange={handleAssetCatSelect}
                        error={fieldHasError("asset_category_id")}
                        value={form.asset_category_id.value}
                        required
                      />
                    </Grid>
                  </Grid>

                  {/* Second row of 3 fields */}
                  <Grid container spacing={3} pt={3}>
                    <Grid item xs={12} md={4}>
                      <BatchSelectInput
                        size="short"
                        value={form.batch_id.value}
                        placeholder={"Select batch Id"}
                        label={"Batch"}
                        handleChange={handleBatchSelect}
                        error={fieldHasError("batch_id")}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <ItemInput
                        label={`Embossment Number ${
                          isTypePhysical ? "" : "(Optional)"
                        }`}
                        placeholder="Enter Embossment Number"
                        value={form.embossment_number?.value}
                        error={
                          (isTypePhysical === true ||
                            isTypePhysical === undefined) &&
                          fieldHasError("embossment_number")
                        }
                        handleChange={updateInputField("embossment_number")}
                        size="full"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <DatePicker
                        handleOnchange={handleDateReceivedPicker}
                        label="Date Recieved"
                        error={fieldHasError("date_received")}
                        errorMessage={form.date_received.errorMessage}
                        value={dayjs(form.date_received.value)}
                        required
                      />
                    </Grid>
                  </Grid>

                  {/* Third row of 3 fields */}
                  <Grid container spacing={3} pt={3}>
                    <Grid item xs={12} md={4}>
                      <DatePicker
                        handleOnchange={handleManufacturedDatePicker}
                        label="Manufactured Date"
                        error={fieldHasError("manufactured_date")}
                        value={dayjs(form.manufactured_date?.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <ItemInput
                        label="Serial Number"
                        placeholder="Enter Serial Number"
                        size="full"
                        value={form.serial_number.value}
                        error={fieldHasError("serial_number")}
                        handleChange={updateInputField("serial_number")}
                        errorMessage={form.serial_number.errorMessage}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <ItemSelectInput
                        label="Brand Name"
                        placeholder="Select Asset Brand"
                        items={ASSET_BRAND_NAME}
                        item=""
                        value={form.brand.value}
                        error={fieldHasError("brand")}
                        handleChange={updateInputField("brand")}
                        errorMessage={form.brand.errorMessage}
                        required
                      />
                    </Grid>
                  </Grid>

                  {/* Fourth row of 3 fields */}
                  <Grid container spacing={3} pt={3}>
                    <Grid item xs={12} md={4}>
                      <ItemInput
                        label="Model"
                        placeholder="Enter model Detail"
                        value={form.model.value}
                        error={fieldHasError("model")}
                        handleChange={updateInputField("model")}
                        errorMessage={form.model.errorMessage}
                        size="full"
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <ItemInput
                        label="Warranty (in Months)"
                        placeholder="Warranty (in Months)"
                        value={form.warranty_in_months?.value}
                        error={fieldHasError("warranty_in_months")}
                        handleChange={updateInputField(
                          "warranty_in_months",
                          "number",
                        )}
                      />
                    </Grid>
                  </Grid>
                  <VerticalGap height={30} />
                </Box>
              </ExpandableContainer>
            </Stack>

            <Divider variant="fullWidth" />

            {/* Additional information  */}
            <Stack direction={"row"} gap={4} p={"24px 0px"}>
              <ExpandableContainer label="Additional Information" initOpenState>
                <Box pb={3}>
                  {assetTagList.length > 0 ? (
                    <Grid
                      container
                      spacing={{ xs: 2, md: 3 }}
                      columns={{ xs: 4, sm: 8, md: 12 }}
                    >
                      {assetTagList.map((tag, index) => (
                        <Grid item xs={2} sm={6} md={4} key={index}>
                          {renderDynamicAssetTagInput(tag, index)}
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      minHeight={200}
                      fontFamily={"Inter"}
                      fontWeight={400}
                    >
                      <span>
                        Select an Asset Category to view additional information
                        fields
                      </span>
                    </Box>
                  )}
                </Box>
              </ExpandableContainer>
            </Stack>
          </Box>
        </Box>
      </Box>
    </MainPageBase>
  );
}

export { UpdateAssetPage };
